import { useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { iniciarPagoMes1Susc, activarSuscMes1Status } from '../lib/portalClienteApi'

// MpSecureFieldsSusc — form de tarjeta con Secure Fields (mp.fields) para el flujo de SUSCRIPCIÓN 3DS
// (flag susc_flujo_mes1 = 'payment_3ds'). Reemplaza al CardPayment Brick SOLO en este form; el one-shot
// de la tienda NO se toca. Ver DISENO-SUSCRIPCION-3DS.md.
//
// Por qué Secure Fields y no el CardPayment Brick: necesitamos DOS tokens frescos de UNA sola entrada
// de tarjeta (mismo card entry → 2× createCardToken):
//   · token A → pago mes1 on-session (Payments API + 3DS)  [edge mp-suscripcion-mes1]
//   · token B → preapproval mes2+ (leg D, authorized)       [edge mp-suscripcion-mes1-status]
// El CardPayment Brick sólo entrega 1 token por submit y no expone el PAN, así que no sirve para esto.
//
// Challenge 3DS: si el pago mes1 vuelve pending_challenge, montamos el Status Screen Brick con el
// payment_id (renderiza el challenge) y hacemos polling a mp-suscripcion-mes1-status hasta que el pago
// quede approved → ahí el edge dispara el leg D con el token B.

const LOG_FRONT_ERROR_URL = 'https://xsviveqhkppmhncodhuv.supabase.co/functions/v1/log-front-error'
const SDK_LOAD_TIMEOUT_MS = 15000
const POLL_INTERVAL_MS = 4000
const POLL_MAX_MS = 6 * 60 * 1000  // el challenge 3DS suele ser ~5 min

function cargarScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some(s => s.src === src)) return resolve()
    const s = document.createElement('script')
    s.src = src; s.async = true; s.onload = resolve
    s.onerror = () => reject(new Error(`No pude cargar ${src}`))
    document.head.appendChild(s)
  })
}

export default function MpSecureFieldsSusc({
  clienteId, monto, montoMes2, emailPagador, userId, sessionId,
  firstName, lastName, identification,
  onActivada, onFallo, onError, onHighRisk,
}) {
  const mpRef = useRef(null)
  const fieldsRef = useRef({})            // { cardNumber, expirationDate, securityCode }
  const statusBrickRef = useRef(null)
  const pollTimerRef = useRef(null)
  const sessionIdRef = useRef(sessionId || (crypto?.randomUUID?.() || String(Date.now())))
  const cardTokenBRef = useRef(null)      // token B guardado para el leg D / polling

  const [cargando, setCargando] = useState(true)
  const [ready, setReady] = useState(false)
  const [procesando, setProcesando] = useState(false)
  const [cardholderName, setCardholderName] = useState(`${firstName || ''} ${lastName || ''}`.trim())
  const [fase, setFase] = useState('form')  // form | challenge | activando
  const [errorLocal, setErrorLocal] = useState(null)

  const logFront = (paso, opts = {}) => {
    try {
      fetch(LOG_FRONT_ERROR_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({
          paso, nivel: opts.nivel || 'error', email: emailPagador || null, user_id: userId || null,
          session_id: sessionIdRef.current, error_code: opts.error_code || null,
          error_msg: (opts.error_msg || '').slice(0, 2000) || null, payload: opts.payload || null,
        }),
      }).catch(() => {})
    } catch { /* noop */ }
  }

  const fail = (msg, code) => {
    setProcesando(false)
    setErrorLocal(msg)
    if (String(code || '').toLowerCase().includes('high_risk')) onHighRisk?.()
    onError?.(msg)
  }

  const stopPolling = () => { if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null } }

  // ---------- init SDK + Secure Fields ----------
  useEffect(() => {
    let cancelado = false
    // App COBROS (la habilitada para Payments API). El token A/B se tokeniza con la PK de Cobros
    // (MP_PUBLIC_KEY_PROD) para que el pago mes1 + el preapproval (ambos con el access token Cobros) matcheen.
    const FALLBACK_PUBLIC_KEY_PROD = 'APP_USR-d3decce5-1e67-4a56-ad5b-1403cb188760'

    async function init() {
      try {
        const { data: cfg } = await supabase.from('app_config').select('key, value')
          .in('key', ['MP_AMBIENTE', 'MP_PUBLIC_KEY_PROD', 'MP_PUBLIC_KEY_TEST'])
        const map = {}
        for (const row of cfg || []) { let v = row.value; try { v = JSON.parse(v) } catch {} map[row.key] = String(v).trim() }
        const ambiente = (map.MP_AMBIENTE || 'prod').toLowerCase()
        let publicKey = ambiente === 'prod' ? map.MP_PUBLIC_KEY_PROD : map.MP_PUBLIC_KEY_TEST
        if (!publicKey && ambiente === 'prod') publicKey = FALLBACK_PUBLIC_KEY_PROD
        if (!publicKey) throw new Error('MP public key no configurada')

        if (!window.MercadoPago) {
          await Promise.race([
            cargarScript('https://sdk.mercadopago.com/js/v2'),
            new Promise((_, rej) => setTimeout(() => rej(new Error('SDK MP timeout')), SDK_LOAD_TIMEOUT_MS)),
          ])
        }
        if (cancelado) return
        if (!window.MercadoPago) throw new Error('SDK MP no disponible')

        const mp = new window.MercadoPago(publicKey, { locale: 'es-UY' })
        mpRef.current = mp
        const style = { height: '100%', fontSize: '16px', placeholderColor: '#94A3B8' }
        fieldsRef.current.cardNumber = mp.fields.create('cardNumber', { style, placeholder: 'Número de tarjeta' }).mount('sf-card-number')
        fieldsRef.current.expirationDate = mp.fields.create('expirationDate', { style, placeholder: 'MM/AA' }).mount('sf-exp')
        fieldsRef.current.securityCode = mp.fields.create('securityCode', { style, placeholder: 'CVV' }).mount('sf-cvv')

        if (!cancelado) { setCargando(false); setReady(true); logFront('sf_ready', { nivel: 'info' }) }
      } catch (e) {
        logFront('sf_init_failed', { error_code: 'sf_init', error_msg: e.message })
        if (!cancelado) { setErrorLocal(e.message); setCargando(false); onError?.(e.message) }
      }
    }
    init()
    return () => {
      cancelado = true
      stopPolling()
      try { Object.values(fieldsRef.current).forEach(f => f?.unmount?.()) } catch {}
      try { statusBrickRef.current?.unmount?.() } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------- doble tokenización + pago mes1 ----------
  async function onSubmit() {
    if (procesando || !ready) return
    setErrorLocal(null); setProcesando(true)
    try {
      if (!cardholderName.trim()) { fail('Ingresá el nombre como figura en la tarjeta'); return }
      // Device ID de MP OBLIGATORIO (antifraude · raíz del caso Marce): sin él el pago sale high_risk.
      // Bloqueamos el submit hasta que security.js haya seteado window.MP_DEVICE_SESSION_ID.
      const deviceSessionId = (typeof window !== 'undefined' && window.MP_DEVICE_SESSION_ID) || null
      if (!deviceSessionId) {
        logFront('sf_no_device_id', { error_code: 'no_device_id', error_msg: 'window.MP_DEVICE_SESSION_ID vacío al submit' })
        fail('Estamos terminando de preparar el pago seguro. Esperá unos segundos y reintentá.')
        return
      }
      const extra = {
        cardholderName: cardholderName.trim(),
        ...(identification?.type ? { identificationType: identification.type } : {}),
        ...(identification?.number ? { identificationNumber: String(identification.number).replace(/\D/g, '') } : {}),
      }
      const mp = mpRef.current

      // 2× createCardToken de la MISMA entrada (QA-gate: el 2º no debe fallar por campos consumidos)
      let tokenA, tokenB
      try {
        tokenA = await mp.fields.createCardToken(extra)
      } catch (e) {
        logFront('sf_token_a_fail', { error_code: 'token_a', error_msg: e?.message || String(e) })
        fail('No pudimos validar la tarjeta. Revisá los datos o probá con otra.'); return
      }
      try {
        tokenB = await mp.fields.createCardToken(extra)
      } catch (e) {
        // Fallback duro: si el 2º token falla (campos consumidos), NO seguimos (no queremos pago mes1
        // sin poder crear el preapproval). Avisamos y el cliente reintenta.
        logFront('sf_token_b_fail', { error_code: 'token_b', error_msg: e?.message || String(e) })
        fail('No pudimos preparar la suscripción. Reintentá cargando la tarjeta de nuevo.'); return
      }
      if (!tokenA?.id || !tokenB?.id) { fail('No pudimos validar la tarjeta. Probá de nuevo.'); return }
      cardTokenBRef.current = tokenB.id
      logFront('sf_two_tokens_ok', { nivel: 'info', payload: { device_id_ok: !!deviceSessionId } })

      // pago mes1 (token A)
      const resp = await iniciarPagoMes1Susc({
        clienteId, email: emailPagador, monto, montoMes2,
        cardTokenId: tokenA.id, firstName, lastName, identification,
        sessionId: sessionIdRef.current, deviceSessionId,
      })

      if (resp?.requiere_challenge && resp?.payment_id) {
        setFase('challenge')
        await montarChallenge(resp.payment_id)
        iniciarPolling()
        return
      }
      if (resp?.status === 'approved') {
        await ejecutarLegD()
        return
      }
      if (resp?.rechazado || resp?.status === 'rejected') {
        fail(mensajeRechazo(resp?.status_detail), resp?.status_detail); return
      }
      if (resp?.requiere_polling) { // in_process u otro pending sin challenge
        setFase('activando'); iniciarPolling(); return
      }
      fail(resp?.error || 'No pudimos procesar el pago. Probá de nuevo.', resp?.status_detail)
    } catch (e) {
      fail(e.message, e.mpCode)
    }
  }

  // ---------- leg D (token B) ----------
  async function ejecutarLegD() {
    setFase('activando')
    try {
      const r = await activarSuscMes1Status({
        clienteId, cardTokenIdB: cardTokenBRef.current, email: emailPagador,
        firstName, lastName, identification, sessionId: sessionIdRef.current,
        deviceSessionId: (typeof window !== 'undefined' && window.MP_DEVICE_SESSION_ID) || null,
      })
      if (r?.activada || r?.ya_activa || r?.estado === 'preapproval_ok') { setProcesando(false); onActivada?.(r); return }
      if (r?.preapproval_fallo) { setProcesando(false); onFallo?.(r); return }
      if (r?.rechazado) { fail(mensajeRechazo(r?.status_detail), r?.status_detail); return }
      // aún no aprobado (raro acá) → seguir polleando
      iniciarPolling()
    } catch (e) { fail(e.message, e.mpCode) }
  }

  // ---------- polling durante/después del challenge ----------
  function iniciarPolling() {
    stopPolling()
    const t0 = Date.now()
    const tick = async () => {
      if (Date.now() - t0 > POLL_MAX_MS) { fail('El proceso de validación expiró. Reintentá con tu tarjeta.'); return }
      try {
        const r = await activarSuscMes1Status({
          clienteId, cardTokenIdB: cardTokenBRef.current, email: emailPagador,
          firstName, lastName, identification, sessionId: sessionIdRef.current,
          deviceSessionId: (typeof window !== 'undefined' && window.MP_DEVICE_SESSION_ID) || null,
        })
        if (r?.activada || r?.ya_activa || r?.estado === 'preapproval_ok') { setProcesando(false); onActivada?.(r); return }
        if (r?.preapproval_fallo) { setProcesando(false); onFallo?.(r); return }
        if (r?.rechazado) { fail(mensajeRechazo(r?.status_detail), r?.status_detail); return }
        // sigue en challenge/pending → reintentar
        pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS)
      } catch (e) {
        // error transitorio de red → seguir intentando hasta el timeout
        pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS)
      }
    }
    pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS)
  }

  async function montarChallenge(paymentId) {
    try {
      const bricks = mpRef.current.bricks()
      statusBrickRef.current = await bricks.create('statusScreen', 'sf-status-brick', {
        initialization: { paymentId: String(paymentId) },
        callbacks: {
          onReady: () => logFront('sf_status_ready', { nivel: 'info' }),
          onError: (err) => logFront('sf_status_error', { error_code: err?.type || 'status_brick', error_msg: err?.message || String(err) }),
        },
      })
    } catch (e) {
      // si el Status Screen Brick no monta, igual seguimos con el polling (el challenge se resuelve
      // del lado de MP; sin UI el cliente no ve el desafío → lo registramos para QA).
      logFront('sf_status_mount_fail', { error_code: 'status_mount', error_msg: e?.message || String(e) })
    }
  }

  function mensajeRechazo(statusDetail) {
    const sd = String(statusDetail || '').toLowerCase()
    if (sd.includes('high_risk')) return 'MP nos pide esperar antes de reintentar por seguridad. Probá con transferencia o esperá 15 minutos.'
    if (sd.includes('insufficient')) return 'La tarjeta no tiene fondos suficientes. Probá con otra.'
    if (sd.includes('cc_rejected_bad_filled')) return 'Revisá los datos de la tarjeta (número, vencimiento o CVV).'
    return 'La tarjeta fue rechazada. Probá con otra tarjeta.'
  }

  return (
    <div className="mp-sf-wrap">
      {cargando && <div className="mp-sf-loading">Cargando pasarela segura…</div>}
      {errorLocal && <div className="mp-sf-error">{errorLocal}</div>}

      {fase === 'form' && (
        <div style={{ display: cargando ? 'none' : 'block' }}>
          <label className="mp-sf-label">Nombre en la tarjeta</label>
          <input
            className="mp-sf-input" type="text" value={cardholderName}
            onChange={e => setCardholderName(e.target.value)} placeholder="Como figura en la tarjeta"
            autoComplete="cc-name" disabled={procesando}
          />
          <label className="mp-sf-label">Número de tarjeta</label>
          <div id="sf-card-number" className="mp-sf-field" />
          <div className="mp-sf-row">
            <div style={{ flex: 1 }}>
              <label className="mp-sf-label">Vencimiento</label>
              <div id="sf-exp" className="mp-sf-field" />
            </div>
            <div style={{ flex: 1 }}>
              <label className="mp-sf-label">CVV</label>
              <div id="sf-cvv" className="mp-sf-field" />
            </div>
          </div>
          <button type="button" className="mp-sf-submit" onClick={onSubmit} disabled={procesando || !ready}>
            {procesando ? 'Procesando…' : 'Guardar tarjeta y activar suscripción'}
          </button>
        </div>
      )}

      {fase === 'challenge' && (
        <div>
          <p className="mp-sf-hint">Tu banco pide una validación extra (3D Secure). Completala en la ventana de abajo — no cierres esta página.</p>
          <div id="sf-status-brick" />
        </div>
      )}

      {fase === 'activando' && (
        <div className="mp-sf-activando">Activando tu suscripción… un momento.</div>
      )}

      <style>{`
        .mp-sf-wrap { min-height: 280px }
        .mp-sf-loading { padding: 24px; text-align: center; color: #64748B; font-size: 14px }
        .mp-sf-error { padding: 12px 14px; background: #FEE2E2; color: #991B1B; border-radius: 8px; font-size: 13px; margin-bottom: 12px }
        .mp-sf-label { display:block; font-size: 12.5px; color: #475569; margin: 12px 0 5px; font-weight: 600 }
        .mp-sf-field { height: 44px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 12px; background: #fff }
        .mp-sf-input { width: 100%; height: 44px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 0 12px; font-size: 16px; box-sizing: border-box }
        .mp-sf-row { display: flex; gap: 12px }
        .mp-sf-submit { width: 100%; margin-top: 18px; height: 48px; border: 0; border-radius: 10px; background: var(--cl-navy, #1A3461); color: #fff; font-weight: 800; font-size: 15px; cursor: pointer }
        .mp-sf-submit:disabled { opacity: .6; cursor: default }
        .mp-sf-hint { font-size: 13.5px; color: #475569; line-height: 1.5; margin: 4px 0 12px }
        .mp-sf-activando { padding: 16px; text-align: center; color: #1e40af; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; font-size: 14px }
      `}</style>
    </div>
  )
}
